import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Box, Stack, Typography, styled } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RouterOutlinedIcon from "@mui/icons-material/RouterOutlined";
import { CustomButton } from "@/components/custom/custom-button";
import CustomSimpleCard from "@/components/custom/custom-simple-card";
import CustomConfirmDialog from "@/components/custom/custom-confirm-dialog";
import { DataTable } from "@/components/custom/data-table";
import { showToast } from "@/redux/actions/toast-actions";
import {
  useSmsGateways, useActiveSmsGateway, useToggleSmsGateway,
} from "@/hooks/use-notifications";
import { getGatewayColumns } from "./_components/gateway-columns";
import GatewayFormDialog from "./_components/gateway-form-dialog";

const Card = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const SmsGateways = () => {
  const dispatch = useDispatch();
  const [dialog, setDialog] = useState({ open: false, gateway: null });
  const [toggleTarget, setToggleTarget] = useState(null);

  const { data: gateways, isLoading, isError } = useSmsGateways();
  const { data: activeGateway, isLoading: activeLoading } = useActiveSmsGateway();
  const toggleMutation = useToggleSmsGateway();

  const columns = useMemo(
    () =>
      getGatewayColumns({
        onEdit: (gateway) => setDialog({ open: true, gateway }),
        onToggle: setToggleTarget,
      }),
    [],
  );

  // Switching the gateway reroutes every outgoing SMS, and disabling the
  // active one stops SMS entirely — confirmed rather than fired on a click.
  const turningOff = toggleTarget?.active;

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <CustomSimpleCard
          title="Active SMS gateway"
          icon={<RouterOutlinedIcon fontSize="small" />}
          isLoading={activeLoading}
        >
          <Typography variant="h6">{activeGateway?.name ?? "None"}</Typography>
        </CustomSimpleCard>
      </Stack>

      <Card>
        <Stack direction="row" justifyContent="flex-end">
          <CustomButton
            startIcon={<AddIcon fontSize="small" />}
            onClick={() => setDialog({ open: true, gateway: null })}
            sx={{ height: 35 }}
          >
            Add Gateway
          </CustomButton>
        </Stack>

        <DataTable
          data={gateways ?? []}
          columns={columns}
          isLoading={isLoading}
          isError={isError}
          noDataMessage="No SMS gateways configured."
        />
      </Card>

      <GatewayFormDialog
        open={dialog.open}
        gateway={dialog.gateway}
        onClose={() => setDialog({ open: false, gateway: null })}
      />

      <CustomConfirmDialog
        open={Boolean(toggleTarget)}
        onClose={() => setToggleTarget(null)}
        title={turningOff ? "Disable this gateway?" : "Make this the active gateway?"}
        description={
          turningOff
            ? `Disabling ${toggleTarget?.name} stops SMS from being sent through it. If no other gateway is active, SMS delivery stops entirely.`
            : `All outgoing SMS will route through ${toggleTarget?.name}. The currently active gateway is replaced.`
        }
        confirmLabel={turningOff ? "Disable gateway" : "Activate gateway"}
        variant={turningOff ? "danger" : "warning"}
        mutation={toggleMutation}
        onConfirm={() =>
          toggleMutation.mutate(toggleTarget.id, {
            onSuccess: (res) =>
              dispatch(showToast(res?.message ?? "Gateway updated", "success")),
            onError: (err) =>
              dispatch(showToast(err?.message ?? "Failed to update gateway", "error")),
          })
        }
      />
    </Stack>
  );
};

export default SmsGateways;